import { useState } from 'react';
import type { PackageResponse } from '../../types/packages';
import PackagePreviewCard from './PackagePreviewCard';
import PackagePreviewDetail from './PackagePreviewDetail';
import { arabicFallbacks, PreviewLocale } from './packagePreviewText';

export interface PackagePhonePreviewProps {
  pkg: PackageResponse;
  garageName: string;
}

/**
 * Spec 081b — the admin copy of the partner phone preview (the portals share no code): the package
 * tile, then the package page, in EN or AR. Arabic falls back to English per field.
 */
export default function PackagePhonePreview(props: PackagePhonePreviewProps) {
  const { pkg, garageName } = props;
  const [locale, setLocale] = useState<PreviewLocale>('en');
  const isAr = locale === 'ar';
  const fallbacks = isAr ? arabicFallbacks(pkg) : [];

  return (
    <div className="offer-preview">
      <div className="offer-preview__toggle" role="group" aria-label="Preview language">
        {(['en', 'ar'] as PreviewLocale[]).map((l) => (
          <button
            key={l}
            type="button"
            className={`offer-preview__toggle-btn${locale === l ? ' offer-preview__toggle-btn--active' : ''}`}
            aria-pressed={locale === l}
            onClick={() => setLocale(l)}
          >
            {l === 'en' ? 'EN' : 'AR'}
          </button>
        ))}
      </div>

      <div className="offer-phone" data-testid="package-phone-preview">
        <div className="offer-phone__notch" />
        <div className="offer-phone__screen" dir={isAr ? 'rtl' : 'ltr'} lang={locale}>
          <PackagePreviewCard pkg={pkg} garageName={garageName} locale={locale} />
          <PackagePreviewDetail pkg={pkg} locale={locale} />
        </div>
      </div>
      {fallbacks.length > 0 && (
        <p className="offer-preview__note">No Arabic {fallbacks.join(', ')}: the app shows the English text.</p>
      )}
    </div>
  );
}
